/**
 * Editor demo (sections/sm-home-editor.liquid): plays the muted demo while ≥35% in view or after a
 * Play click; pauses off screen, in a hidden tab, on a manual pause, and always under reduced motion.
 */
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const VISIBLE = 0.35;

class SmHomeEditor extends HTMLElement {
  #abort;
  #observer;
  #inView = false;
  #userPaused = false;
  #userPlayed = false;
  #failed = false;

  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-sm-home-editor-toggle]');
    if (!this.video || !this.toggle) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const on = (target, type, handler) => target.addEventListener(type, handler, { signal });

    for (const type of ['loadeddata', 'play', 'pause']) on(this.video, type, () => this.#syncUi());
    // A failing <source> fires `error` on itself, not on the video. Only the last one means nothing is
    // left to try: on phones the desktop file is still the fallback for a broken mobile one.
    const lastSource = this.video.querySelector('source:last-of-type');
    if (lastSource) on(lastSource, 'error', () => this.#fail());
    on(this.video, 'error', () => this.#fail());
    on(this.toggle, 'click', () => {
      this.#userPaused = !this.video.paused;
      this.#userPlayed = !this.#userPaused;
      this.#sync();
    });
    on(document, 'visibilitychange', () => this.#sync());
    on(REDUCED_MOTION, 'change', () => this.#sync());

    // The ratio, not isIntersecting: engines that follow the spec report any overlap as intersecting.
    // A Play click beats the 35% rule until the next crossing; threshold 0 stops it fully off screen.
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#inView = entry.intersectionRatio >= VISIBLE;
        this.#userPlayed = false;
        this.#sync();
      },
      { threshold: [0, VISIBLE] }
    );
    this.#observer.observe(this);
    this.#sync();
    // This module runs after parsing, so every source may already have failed with no event left.
    setTimeout(() => {
      const { networkState, readyState } = this.video;
      if (networkState === HTMLMediaElement.NETWORK_NO_SOURCE && readyState === 0) this.#fail();
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.video?.pause();
  }

  #sync() {
    const reduced = REDUCED_MOTION.matches;
    this.toggle.hidden = reduced || this.#failed;
    const shouldPlay =
      (this.#inView || this.#userPlayed) &&
      !this.#userPaused &&
      !document.hidden &&
      !reduced &&
      !this.#failed;
    if (shouldPlay && this.video.paused) {
      // AbortError = a pause() overtook the play(); NotAllowedError = autoplay blocked: stay on "Play"
      // without counting it as the user's pause. NotSupportedError = no playable source.
      this.video
        .play()
        .catch((error) => (error.name === 'NotSupportedError' ? this.#fail() : this.#syncUi()));
    } else if (!shouldPlay && !this.video.paused) {
      this.video.pause();
    }
    this.#syncUi();
  }

  #syncUi() {
    const paused = this.video.paused;
    const { labelPlay, labelPause } = this.toggle.dataset;
    this.classList.toggle('is-paused', paused);
    this.classList.toggle(
      'is-ready',
      this.video.readyState >= 2 && !REDUCED_MOTION.matches && !this.#failed
    );
    this.toggle.setAttribute('aria-label', paused ? labelPlay : labelPause);
  }

  #fail() {
    this.#failed = true;
    this.#sync();
  }
}

if (!customElements.get('sm-home-editor')) customElements.define('sm-home-editor', SmHomeEditor);
